import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import SEO from "./components/SEO";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import Home from "./pages/Home";
import Services from "./pages/Services";
import Zones from "./pages/Zones";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Devis from "./pages/Devis";
import AdminBlog from "./pages/AdminBlog";
import LegalNotice from "./pages/LegalNotice";

// Services Pages
import ServiceMaison from "./pages/services/ServiceMaison";
import ServiceAppartement from "./pages/services/ServiceAppartement";
import ServiceCaveGrenier from "./pages/services/ServiceCaveGrenier";
import ServiceProfessionnel from "./pages/services/ServiceProfessionnel";
import ServiceSuccession from "./pages/services/ServiceSuccession";

// Zones Pages
import ZoneAnnecy from "./pages/zones/ZoneAnnecy";
import ZoneGenevois from "./pages/zones/ZoneGenevois";
import ZoneMontBlanc from "./pages/zones/ZoneMontBlanc";
import ZoneAixChambery from "./pages/zones/ZoneAixChambery";
import ZonePaysArve from "./pages/zones/ZonePaysArve";
import ZonePaysGex from "./pages/zones/ZonePaysGex";

// Local SEO Pages
import DebarrasMaisonAnnecy from "./pages/local/DebarrasMaisonAnnecy";
import DebarrasAppartementAnnecy from "./pages/local/DebarrasAppartementAnnecy";
import DebarrasSuccessionAnnecy from "./pages/local/DebarrasSuccessionAnnecy";
import VideMaisonAnnecy from "./pages/local/VideMaisonAnnecy";

// Static Blog Posts
import BlogPrixDebarras from "./pages/blog/BlogPrixDebarras";
import BlogSuccessionAnnecy from "./pages/blog/BlogSuccessionAnnecy";
import BlogViderMaisonParents from "./pages/blog/BlogViderMaisonParents";

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// 404 Page
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
      <SEO title="Page introuvable | DEBECO" description="La page que vous recherchez n'existe pas." noindex={true} />
      <div className="text-center px-4">
        <h1 className="text-6xl font-bold text-blue-900 mb-4" data-testid="404-title">404</h1>
        <p className="text-xl text-gray-600 mb-8">La page que vous recherchez n'existe pas.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/" className="inline-flex items-center justify-center px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors font-medium">Retour à l'accueil</Link>
          <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-lg hover:bg-blue-50 transition-colors font-medium">Nous contacter</Link>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/maison" element={<ServiceMaison />} />
              <Route path="/services/appartement" element={<ServiceAppartement />} />
              <Route path="/services/cave-grenier" element={<ServiceCaveGrenier />} />
              <Route path="/services/professionnel" element={<ServiceProfessionnel />} />
              <Route path="/services/succession" element={<ServiceSuccession />} />
              <Route path="/zones" element={<Zones />} />
              <Route path="/zones/annecy" element={<ZoneAnnecy />} />
              <Route path="/zones/genevois" element={<ZoneGenevois />} />
              <Route path="/zones/mont-blanc" element={<ZoneMontBlanc />} />
              <Route path="/zones/aix-chambery" element={<ZoneAixChambery />} />
              <Route path="/zones/pays-arve" element={<ZonePaysArve />} />
              <Route path="/zones/pays-gex" element={<ZonePaysGex />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/prix-debarras-maison-annecy" element={<BlogPrixDebarras />} />
              <Route path="/blog/debarras-succession-annecy-guide" element={<BlogSuccessionAnnecy />} />
              <Route path="/blog/vider-maison-parents-conseils" element={<BlogViderMaisonParents />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/devis" element={<Devis />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/mentions-legales" element={<LegalNotice />} />
              <Route path="/nos-valeurs" element={<Navigate to="/a-propos" replace />} />
              <Route path="/debarras-maison-annecy" element={<DebarrasMaisonAnnecy />} />
              <Route path="/debarras-appartement-annecy" element={<DebarrasAppartementAnnecy />} />
              <Route path="/debarras-succession-annecy" element={<DebarrasSuccessionAnnecy />} />
              <Route path="/vide-maison-annecy" element={<VideMaisonAnnecy />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
          <Toaster />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
